import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useAuth } from '../../Auth/AuthContext';
import { auth, db } from '../../../config/firebase';
import { EmailAuthProvider, reauthenticateWithCredential, deleteUser } from 'firebase/auth';
import { collection, query, where, getDocs, doc, getDoc, arrayRemove, writeBatch } from 'firebase/firestore';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { FirebaseService } from '../../../services/FirebaseService';

interface DeleteAccountDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({ 
  open, 
  onClose, 
  onSuccess 
}) => {
  const { user } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [isInCompany, setIsInCompany] = useState(false);

  useEffect(() => {
    const checkOwnership = async () => {
      try {
        const profile = await FirebaseService.fetchUserProfile();
        setIsOwner(profile?.companyRole === 'owner');
        setIsInCompany(!!profile?.companyId);
      } catch (error) {
        console.error('Error checking ownership:', error);
      }
    };

    if (open && user) {
      checkOwnership();
    }
  }, [open, user]);

  const handleClose = () => {
    if (!isDeleting) {
      setPassword('');
      setConfirmed(false);
      setError(null);
      onClose();
    }
  };

  const handleDelete = async () => {
    if (!user || !user.email) {
      setError('User not authenticated');
      return;
    }

    if (!password) {
      setError('Password is required');
      return;
    }

    if (!confirmed) {
      setError('Please confirm that you understand this action');
      return;
    }

    setIsDeleting(true);
    setError(null);

    // Track what we've deleted for potential rollback
    const deletedStorageRefs: string[] = [];
    let authDeleted = false;

    try {
      // Get current Firebase Auth user
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        setError('User not authenticated');
        setIsDeleting(false);
        return;
      }

      // Re-authenticate user
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);

      const userId = currentUser.uid;
      const storage = getStorage();

      // Get user profile to check company status
      const profile = await FirebaseService.fetchUserProfile();
      const companyId = profile?.companyId;
      const isOwnerUser = profile?.companyRole === 'owner';

      // Block company owners
      if (isOwnerUser && companyId) {
        setError('Company owners cannot delete their account without Cloud Functions. Please contact support or transfer ownership first.');
        setIsDeleting(false);
        return;
      }

      console.log('🔒 Starting ACID deletion process...');

      // PHASE 1: Collect all data to delete (read-only phase)
      console.log('� Phase 1: Collecting data...');
      const reportsQuery = query(collection(db, `users/${userId}/reports`));
      const reportsSnapshot = await getDocs(reportsQuery);
      
      const dataToDelete = {
        reports: [] as any[],
        entries: [] as any[],
        images: [] as string[],
        profileImage: profile?.profileImagePath || null,
        companyMember: null as any
      };

      // Collect reports and entries
      for (const reportDoc of reportsSnapshot.docs) {
        dataToDelete.reports.push({
          id: reportDoc.id,
          ref: reportDoc.ref
        });

        const entriesQuery = query(
          collection(db, `users/${userId}/entries`),
          where('report_id', '==', reportDoc.id)
        );
        const entriesSnapshot = await getDocs(entriesQuery);

        for (const entryDoc of entriesSnapshot.docs) {
          const entryData = entryDoc.data();
          dataToDelete.entries.push({
            id: entryDoc.id,
            ref: entryDoc.ref
          });

          // Collect image URLs
          if (entryData.images && Array.isArray(entryData.images)) {
            dataToDelete.images.push(...entryData.images);
          }
          if (entryData.photo_url) {
            dataToDelete.images.push(entryData.photo_url);
          }
        }
      }

      // Get company member data if in company
      if (companyId && !isOwnerUser) {
        const companyRef = doc(db, 'companies', companyId);
        const companyDoc = await getDoc(companyRef);
        if (companyDoc.exists()) {
          const companyData = companyDoc.data();
          const memberToRemove = companyData.members?.find((m: any) => m.user_id === userId);
          if (memberToRemove) {
            dataToDelete.companyMember = {
              companyRef,
              member: memberToRemove
            };
          }
        }
      }

      console.log(`📊 Data collected: ${dataToDelete.reports.length} reports, ${dataToDelete.entries.length} entries, ${dataToDelete.images.length} images`);

      // PHASE 2: Delete Storage files (can't be in batch)
      console.log('🗑️ Phase 2: Deleting Storage files...');
      try {
        // Delete entry images
        for (const imageUrl of dataToDelete.images) {
          const imagePath = extractStoragePathFromUrl(imageUrl);
          if (imagePath) {
            try {
              const imageRef = ref(storage, imagePath);
              await deleteObject(imageRef);
              deletedStorageRefs.push(imagePath);
            } catch (err) {
              console.warn('Failed to delete image:', imagePath, err);
              // Continue with other deletions
            }
          }
        }

        // Delete profile image
        if (dataToDelete.profileImage) {
          try {
            const profileRef = ref(storage, dataToDelete.profileImage);
            await deleteObject(profileRef);
            deletedStorageRefs.push(dataToDelete.profileImage);
          } catch (err) {
            console.warn('Failed to delete profile image:', err);
          }
        }
      } catch (storageError) {
        console.error('Storage deletion failed:', storageError);
        throw new Error('Failed to delete images. Aborting account deletion.');
      }

      // PHASE 3: Firestore batch delete (atomic)
      console.log('🔒 Phase 3: Executing atomic Firestore batch...');
      const batch = writeBatch(db);

      // Add all entry deletions to batch
      for (const entry of dataToDelete.entries) {
        batch.delete(entry.ref);
      }

      // Add all report deletions to batch
      for (const report of dataToDelete.reports) {
        batch.delete(report.ref);
      }

      // Remove from company members
      if (dataToDelete.companyMember) {
        batch.update(dataToDelete.companyMember.companyRef, {
          members: arrayRemove(dataToDelete.companyMember.member)
        });
      }

      // Delete user profile
      batch.delete(doc(db, 'users', userId));

      // Commit the batch (all-or-nothing)
      await batch.commit();
      console.log('✅ Firestore batch committed successfully');

      // PHASE 4: Delete Auth account (point of no return)
      console.log('� Phase 4: Deleting auth account...');
      await deleteUser(currentUser);
      authDeleted = true;
      console.log('✅ Account deletion complete!');

      // Success
      onSuccess();

    } catch (error: any) {
      console.error('❌ Error during deletion:', error);

      // If auth was already deleted, we can't rollback
      if (authDeleted) {
        console.error('⚠️ Auth deleted but error occurred. User is signed out.');
        onSuccess(); // Still call success since user is deleted
        return;
      }

      // Provide user-friendly error
      if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else if (error.code === 'permission-denied') {
        setError('Permission denied. Some data could not be deleted.');
      } else if (error.message) {
        setError(error.message);
      } else {
        setError('Failed to delete account. Please try again or contact support.');
      }

      setIsDeleting(false);
    }
  };

  // Helper function to extract storage path from URL
  const extractStoragePathFromUrl = (url: string): string | null => {
    try {
      const urlParts = url.split('/o/')[1];
      if (urlParts) {
        const pathPart = urlParts.split('?')[0];
        return decodeURIComponent(pathPart);
      }
      return null;
    } catch (error) {
      console.error('Error extracting storage path:', error);
      return null;
    }
  };

  const getWarningMessage = () => {
    if (isOwner) {
      return (
        <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            ⚠️ Company Owner Limitation
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            As a company owner, you cannot delete your account directly. You must either:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>Transfer ownership to another member first, OR</li>
            <li>Delete your company (which will remove all company data)</li>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
            Please go to Company Settings to manage your company.
          </Typography>
        </Alert>
      );
    } else if (isInCompany) {
      return (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            Your company membership will be removed. Shared company reports will remain accessible 
            to your company, but your personal reports will be deleted permanently.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
            This action cannot be undone.
          </Typography>
        </Alert>
      );
    } else {
      return (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            All your reports and data will be permanently deleted.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
            This action cannot be undone.
          </Typography>
        </Alert>
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: isOwner ? '2px solid #d32f2f' : '1px solid #e0e0e0'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: isOwner ? '#ffebee' : '#fff3e0',
        color: isOwner ? '#d32f2f' : '#e65100'
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon />
          <Typography variant="h6" component="span">
            Delete Account
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {getWarningMessage()}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          type="password"
          label="Enter your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={isDeleting}
          margin="normal"
          placeholder="Password"
          helperText="Enter your password to confirm deletion"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              disabled={isDeleting}
              color="error"
            />
          }
          label={
            <Typography variant="body2">
              I understand this action cannot be undone
            </Typography>
          }
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={isDeleting}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          disabled={!password || !confirmed || isDeleting || isOwner}
          variant="contained"
          color="error"
          startIcon={isDeleting ? <CircularProgress size={20} /> : null}
        >
          {isDeleting ? 'Deleting...' : isOwner ? 'Cannot Delete' : 'Delete Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
